import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { SectionHead } from '../SectionHead';
import { EmployeeAvatar } from '../../Shared/EmployeeAvatar';
import type { UtilizationRow, HeatCellData } from '../../../types/dashboard';

interface Props {
  matrix: UtilizationRow[];
  weekDayIso: string[];
  weekNumber: number;
}

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function heatColor(cell: HeatCellData): { bg: string; text: string } {
  if (cell.state === 'off') return { bg: 'bg-surface-muted dark:bg-slate-800/50', text: 'text-ink-faint' };
  if (cell.state === 'absence')
    return { bg: 'bg-accent/15 dark:bg-accent/20', text: 'text-accent-700 dark:text-accent-200' };
  const ratio = cell.ratio ?? 0;
  if (ratio >= 0.95) return { bg: 'bg-status-full', text: 'text-white' };
  if (ratio >= 0.85) return { bg: 'bg-status-partial', text: 'text-white' };
  return { bg: 'bg-status-under', text: 'text-white' };
}

export function UtilizationHeatmap({ matrix, weekDayIso, weekNumber }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead
        kicker={t('admin.heatmap.kicker')}
        title={t('admin.heatmap.title', { week: weekNumber })}
      />

      <div className="mt-4 overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Header row */}
          <div className="grid grid-cols-[10rem_repeat(7,minmax(0,1fr))] gap-1 mb-1 text-[10px] uppercase tracking-[0.15em] font-mono-num text-ink-faint dark:text-slate-500">
            <div />
            {weekDayIso.map((iso, idx) => {
              const d = new Date(iso + 'T00:00:00');
              return (
                <div key={iso} className="text-center">
                  <div>{DAY_LABELS[idx]}</div>
                  <div className="text-ink-subtle dark:text-slate-400 mt-0.5">
                    {String(d.getDate()).padStart(2, '0')}.
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rows */}
          {matrix.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface-muted dark:bg-slate-800/50 dark:border-slate-700 p-6 text-center text-sm text-ink-muted dark:text-slate-400">
              {t('admin.today.empty')}
            </div>
          ) : (
            matrix.map((row) => (
              <div
                key={row.employee.id}
                className="grid grid-cols-[10rem_repeat(7,minmax(0,1fr))] gap-1 items-center mb-1"
              >
                <div className="flex items-center gap-2 min-w-0 pr-2 sticky left-0 bg-surface dark:bg-slate-900">
                  <EmployeeAvatar
                    employee={{
                      firstName: row.employee.firstName,
                      lastName: row.employee.lastName,
                    }}
                    size={24}
                  />
                  <span className="text-xs text-ink dark:text-slate-100 truncate">
                    {row.employee.firstName} {row.employee.lastName}
                  </span>
                </div>
                {row.days.map((cell, idx) => {
                  const palette = heatColor(cell);
                  return (
                    <div
                      key={`${row.employee.id}-${idx}`}
                      className={`relative h-9 rounded ${palette.bg} flex items-center justify-center transition-colors hover:outline hover:outline-2 hover:outline-ink/20`}
                      aria-label={`${row.employee.firstName} ${row.employee.lastName}, ${DAY_LABELS[idx]}: ${cell.hours.toFixed(1)}h / ${cell.target.toFixed(1)}h`}
                      title={
                        cell.state === 'absence'
                          ? cell.absenceLabel ?? t('admin.heatmap.legend.absence')
                          : cell.state === 'work'
                          ? `${cell.hours.toFixed(1)}h / ${cell.target.toFixed(1)}h`
                          : t('admin.heatmap.legend.off')
                      }
                    >
                      {cell.state === 'work' && (
                        <span className={`font-mono-num text-[10px] font-semibold ${palette.text}`}>
                          {cell.hours > 0 ? cell.hours.toFixed(1).replace('.', ',') : '–'}
                        </span>
                      )}
                      {cell.state === 'absence' && (
                        <span className="font-mono-num text-[10px] font-semibold">A</span>
                      )}
                      {cell.conflict && (
                        <AlertTriangle
                          className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-status-conflict"
                          aria-hidden
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-ink-muted dark:text-slate-400 font-mono-num">
        <LegendSwatch className="bg-status-full" label={t('admin.heatmap.legend.high')} />
        <LegendSwatch className="bg-status-partial" label={t('admin.heatmap.legend.mid')} />
        <LegendSwatch className="bg-status-under" label={t('admin.heatmap.legend.low')} />
        <LegendSwatch className="bg-accent/30" label={t('admin.heatmap.legend.absence')} />
        <LegendSwatch className="bg-surface-sunken dark:bg-slate-800" label={t('admin.heatmap.legend.off')} />
      </div>
    </div>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded ${className}`} aria-hidden />
      {label}
    </span>
  );
}
